import React, { useState } from 'react';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { router, useLocalSearchParams } from 'expo-router';

import { AppointmentStatusPill } from '@/components/schedule/appointment-status-pill';
import { ThemedText } from '@/components/themed-text';
import { Card, PressableCard } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { PageLayout } from '@/components/ui/page-layout';
import { PressableScale } from '@/components/ui/pressable-scale';
import { SkeletonCard } from '@/components/ui/skeleton';
import { useCancelAppointment, useClientAppointments } from '@/hooks/use-appointments';
import { useToastStore } from '@/store/toastStore';
import { APPOINTMENT_STATUS } from '@/types/appointment';
import { LOCATION_MODE } from '@/types/location-mode';
import { getApiErrorMessage } from '@/utils/api-error';
import {
  openAddressInMaps,
  openMeetingUrl,
  resolveAppointmentLocation,
} from '@/utils/appointment-location';
import { formatAppointmentWhen, formatDuration, formatSlotRange } from '@/utils/format-appointment';
import { formatCurrency } from '@/utils/format-currency';

const ACTIONABLE = [APPOINTMENT_STATUS.PENDING, APPOINTMENT_STATUS.CONFIRMED];

const LOCATION_HINT: Record<LOCATION_MODE, string> = {
  [LOCATION_MODE.PROVIDER_SPACE]: 'Você vai até o profissional.',
  [LOCATION_MODE.CLIENT_ADDRESS]: 'O profissional vai até você.',
  [LOCATION_MODE.ONLINE]: 'O profissional vai enviar o link quando estiver disponível.',
};

function InfoRow({
  icon,
  label,
  value,
}: {
  icon: React.ComponentProps<typeof Ionicons>['name'];
  label: string;
  value: string;
}) {
  return (
    <View className="flex-row items-start gap-3">
      <View className="h-9 w-9 items-center justify-center rounded-full bg-neutral-100">
        <Ionicons className="text-neutral-600" name={icon} size={16} />
      </View>
      <View className="min-w-0 flex-1 gap-0.5">
        <ThemedText className="text-xs text-neutral-500">{label}</ThemedText>
        <ThemedText className="font-semibold text-sm text-foreground">{value}</ThemedText>
      </View>
    </View>
  );
}

export default function ClientAppointmentDetailScreen() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { addToast } = useToastStore();
  const { data, isLoading } = useClientAppointments();
  const cancel = useCancelAppointment();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const appointment = data?.find((a) => a.id === id);
  const back = () => (router.canGoBack() ? router.back() : router.replace('/(tabs)/(client)/hoje'));

  if (!appointment) {
    return (
      <PageLayout title="Agendamento" onBack={back}>
        {isLoading ? (
          <SkeletonCard showHeader lines={3} />
        ) : (
          <EmptyState
            icon="calendar-outline"
            title="Agendamento não encontrado"
            description="Ele pode ter sido removido."
            actionLabel="Ver meus agendamentos"
            onAction={() => router.replace('/(tabs)/(client)/appointments')}
          />
        )}
      </PageLayout>
    );
  }

  const isFuture = new Date(appointment.startAt).getTime() > Date.now();
  const canManage = isFuture && ACTIONABLE.includes(appointment.status);
  const location = resolveAppointmentLocation(appointment);
  const minutes = Math.round(
    (new Date(appointment.endAt).getTime() - new Date(appointment.startAt).getTime()) / 60000,
  );

  const handleReschedule = () =>
    router.push({
      pathname: '/(tabs)/(client)/appointments/reschedule/[id]',
      params: {
        id: appointment.id,
        providerId: appointment.providerId,
        serviceId: appointment.serviceId,
        serviceName: appointment.serviceName ?? 'Serviço',
      },
    });

  const confirmCancel = () =>
    cancel.mutate(
      { id: appointment.id },
      {
        onSuccess: () => addToast('Agendamento cancelado.', 'success'),
        onError: (err: any) =>
          addToast(
            getApiErrorMessage(err, 'Fora do prazo de cancelamento. Fale com o prestador.'),
            'error',
          ),
      },
    );

  return (
    <>
      <PageLayout
        title={appointment.serviceName ?? 'Agendamento'}
        subtitle={appointment.providerName ?? undefined}
        onBack={back}
      >
        <Card className="gap-4 p-4">
          <View className="flex-row items-center justify-between gap-3">
            <ThemedText className="flex-1 font-semibold text-base">
              {formatAppointmentWhen(appointment.startAt)}
            </ThemedText>
            <AppointmentStatusPill status={appointment.status} />
          </View>
          <InfoRow
            icon="time-outline"
            label="Horário"
            value={[
              formatSlotRange(appointment.startAt, appointment.endAt),
              minutes > 0 ? formatDuration(minutes) : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          />
          {appointment.price != null && (
            <InfoRow
              icon="wallet-outline"
              label="Valor"
              value={formatCurrency(appointment.price)}
            />
          )}
          {!!appointment.enrollmentId && (
            <InfoRow icon="repeat" label="Recorrência" value="Faz parte do seu plano recorrente" />
          )}
          {!!appointment.cancelReason && appointment.status === APPOINTMENT_STATUS.CANCELLED && (
            <InfoRow
              icon="close-circle-outline"
              label="Motivo do cancelamento"
              value={appointment.cancelReason}
            />
          )}
        </Card>

        {!!location && (
          <Card className="gap-3 p-4">
            <InfoRow icon={location.icon} label="Onde" value={location.label} />
            {!!location.mode && !location.meetingUrl && (
              <ThemedText className="text-xs text-neutral-500">
                {LOCATION_HINT[location.mode]}
              </ThemedText>
            )}
            {!!location.address && (
              <PressableScale
                onPress={() => openAddressInMaps(location.address!)}
                className="flex-row items-center justify-center gap-1.5 rounded-full border border-primary/15 bg-primary/5 py-2.5"
              >
                <Ionicons name="navigate-outline" size={16} color="#FD5001" />
                <ThemedText className="font-semibold text-sm text-primary">
                  Abrir no mapa
                </ThemedText>
              </PressableScale>
            )}
            {!!location.meetingUrl && (
              <PressableScale
                onPress={() => openMeetingUrl(location.meetingUrl!)}
                className="flex-row items-center justify-center gap-1.5 rounded-full bg-primary py-2.5"
              >
                <Ionicons name="videocam" size={16} color="#FFFFFF" />
                <ThemedText className="font-semibold text-sm text-white">Entrar na chamada</ThemedText>
              </PressableScale>
            )}
          </Card>
        )}

        <PressableCard
          className="flex-row items-center gap-3 p-4"
          onPress={() =>
            router.push({
              pathname: '/(tabs)/(client)/service/[serviceId]',
              params: { serviceId: appointment.serviceId },
            })
          }
        >
          <Ionicons className="text-neutral-600" name="pricetag-outline" size={18} />
          <ThemedText className="flex-1 font-semibold text-sm">Ver o serviço</ThemedText>
          <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
        </PressableCard>

        {!!appointment.enrollmentId && (
          <PressableCard
            className="flex-row items-center gap-3 p-4"
            onPress={() =>
              router.push({
                pathname: '/(tabs)/(client)/enrollments-client/[id]',
                params: { id: appointment.enrollmentId! },
              })
            }
          >
            <Ionicons className="text-neutral-600" name="document-text-outline" size={18} />
            <ThemedText className="flex-1 font-semibold text-sm">Ver o contrato</ThemedText>
            <Ionicons className="text-neutral-400" name="chevron-forward" size={16} />
          </PressableCard>
        )}

        {canManage && (
          <View className="flex-row gap-2">
            <PressableScale
              onPress={handleReschedule}
              className="flex-1 flex-row items-center justify-center gap-1.5 rounded-full border border-neutral-200 bg-card py-2.5"
            >
              <Ionicons className="text-neutral-600" name="swap-horizontal-outline" size={16} />
              <ThemedText className="font-semibold text-sm text-neutral-700">Remarcar</ThemedText>
            </PressableScale>
            <PressableScale
              onPress={() => setConfirmOpen(true)}
              disabled={cancel.isPending}
              className="flex-1 flex-row items-center justify-center gap-1.5 rounded-full border border-red-100 bg-red-50 py-2.5"
            >
              <Ionicons className="text-red-700" name="close-outline" size={16} />
              <ThemedText className="font-semibold text-sm text-red-700">Cancelar</ThemedText>
            </PressableScale>
          </View>
        )}
      </PageLayout>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title="Cancelar agendamento"
        message={`Deseja cancelar ${appointment.serviceName ?? 'este agendamento'}?`}
        confirmLabel="Sim, cancelar"
        cancelLabel="Voltar"
        destructive
        onConfirm={confirmCancel}
      />
    </>
  );
}
